import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { fetchMe, logout, refreshSession } from '@/lib/api/auth';
import { queryKeys } from '@/lib/api/queryClient';
import type { CustomerProfile } from '@/lib/api/types';

import { SessionContext, type SessionApi, type SessionStatus } from './sessionContext';

/** Refresh this many seconds before the access token expires. */
const REFRESH_MARGIN_SECONDS = 60;

interface SessionState {
  status: SessionStatus;
  user: CustomerProfile | null;
}

/**
 * Restores the session on load and keeps the access token alive.
 *
 * A reload throws the in-memory access token away by design, so the first thing
 * the app does is offer the httpOnly refresh cookie back to the server. A 401
 * there is not an error — it is what "nobody is signed in" looks like, and it is
 * the state most visitors start in, so it must not produce an error screen or a
 * console warning.
 *
 * The scheduled re-refresh exists because a checkout takes longer than a
 * fifteen-minute access token: without it, a customer who reads the price
 * breakdown carefully gets a 401 when they press Continue.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({ status: 'loading', user: null });
  const queryClient = useQueryClient();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Declared as a ref-held function because `schedule` and `renew` reference
  // each other; a ref breaks the cycle without a useCallback dependency knot.
  const scheduleRef = useRef<(expiresInSeconds: number) => void>(() => undefined);

  const renew = useCallback(async () => {
    try {
      const token = await refreshSession();
      if (!mountedRef.current) return;
      scheduleRef.current(token.expires_in_seconds);
    } catch {
      // The refresh family is gone (expired, revoked, or rotated behind us).
      // Drop to anonymous quietly; the next protected action will ask them to
      // sign in with a message that makes sense at that moment.
      if (mountedRef.current) setState({ status: 'anonymous', user: null });
    }
  }, []);

  scheduleRef.current = (expiresInSeconds: number) => {
    clearTimer();
    const delay = Math.max(30, expiresInSeconds - REFRESH_MARGIN_SECONDS) * 1000;
    timerRef.current = setTimeout(() => void renew(), delay);
  };

  useEffect(() => {
    mountedRef.current = true;
    let cancelled = false;

    const bootstrap = async () => {
      try {
        const token = await refreshSession();
        const profile = await fetchMe();
        if (cancelled) return;
        setState({ status: 'authenticated', user: profile });
        scheduleRef.current(token.expires_in_seconds);
      } catch {
        if (cancelled) return;
        setState({ status: 'anonymous', user: null });
      }
    };

    void bootstrap();

    return () => {
      cancelled = true;
      mountedRef.current = false;
      clearTimer();
    };
  }, [clearTimer]);

  // Whose basket it is changes with the session — signing in claims the
  // anonymous cart, signing out gives it up — and the header shows a count of
  // it on every page. Re-read it rather than let the old owner's number stand.
  const rereadBasket = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.currentBasket() });
  }, [queryClient]);

  const signIn = useCallback(
    (user: CustomerProfile, expiresInSeconds: number) => {
      setState({ status: 'authenticated', user });
      scheduleRef.current(expiresInSeconds);
      rereadBasket();
    },
    [rereadBasket],
  );

  const signOut = useCallback(async () => {
    clearTimer();
    await logout();
    setState({ status: 'anonymous', user: null });
    rereadBasket();
  }, [clearTimer, rereadBasket]);

  const value = useMemo<SessionApi>(
    () => ({ status: state.status, user: state.user, signIn, signOut }),
    [state.status, state.user, signIn, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
